/**
 * Pure logic for the Glass-Over-World snapshot backdrop helper.
 *
 * Lives apart from snapshotBackdrop.ts so vitest (Node-only, no jsdom)
 * can exercise validation, the capture state machine, the
 * flash-overlay pre-filter, ownership-transfer planning, and
 * unique-key generation without importing Phaser at runtime. Mirrors
 * the dialogueOverlayLogic.ts / motion.ts split established in this
 * repo.
 *
 * The Phaser-wired shell (snapshotBackdrop.ts) wraps these primitives
 * around scene.events.once('render'), DynamicTexture allocation, and
 * GameObjects.Image construction.
 */

export type CaptureState = 'idle' | 'awaiting' | 'ready' | 'timeout' | 'error';
export type CaptureEvent = 'START' | 'COMPLETE' | 'TIMEOUT' | 'ERROR';

export interface ChildLike {
  readonly name?: string;
}

/**
 * The capture helper rejects any source whose display list still
 * contains the encounter-flash GameObject. PR 2 will set
 * `.setName('flashOverlay')` on the existing flash sprite and switch
 * its tween from `setAlpha(0)` to `destroy()` on complete; this
 * predicate is the contract PR 2 satisfies (eng-locked #7).
 *
 * An `alpha=0` flash sprite still occupies the display list and would
 * end up white-saturating the captured texture. The helper refuses to
 * proceed in that case — programmer error, not a runtime fallback.
 */
export function hasFlashOverlay(children: readonly ChildLike[]): boolean {
  for (const c of children) {
    if (c?.name === 'flashOverlay') return true;
  }
  return false;
}

export interface ValidatableScene {
  readonly children: { readonly list: readonly ChildLike[] };
}

export interface CaptureRequest {
  readonly width: number;
  readonly height: number;
  readonly textureKey: string;
  readonly timeoutMs: number;
  readonly depth: number;
}

export interface ValidateCaptureOpts {
  readonly width: number;
  readonly height: number;
  readonly textureKey: string;
  readonly timeoutMs?: number;
  readonly depth?: number;
}

const DEFAULT_DEPTH = 0;

/**
 * Default capture timeout, scaled with canvas area. The race target is
 * renderer.snapshot's FULL pipeline — end-of-frame wait, synchronous
 * gl.readPixels + per-pixel copy + PNG encode, then an async Image
 * decode — whose cost is linear in pixel count and main-thread
 * blocking (the expired timer macrotask dispatches before image.onload
 * once the encode crosses the deadline). A flat 100ms fit the old
 * one-frame race but silently fails QHD/4K desktop canvases on mid
 * hardware. Curve: 300ms floor (mobile ~0.3MP lands here) + 100ms per
 * megapixel (QHD ≈ 660ms, 4K ≈ 1.1s), capped at 2s so a genuinely
 * halted game loop still falls back promptly.
 */
export function defaultTimeoutForArea(width: number, height: number): number {
  const base = 300 + Math.ceil((width * height) / 10_000);
  return Math.min(2_000, base);
}

/**
 * Validate inputs and produce a CaptureRequest descriptor. Throws on:
 *   - nil sourceScene (programmer error)
 *   - flashOverlay still present in display list (PR 2 contract)
 *   - non-positive width/height (degenerate viewport)
 *   - empty textureKey
 */
export function validateCaptureInputs(
  sourceScene: ValidatableScene | null | undefined,
  opts: ValidateCaptureOpts,
): CaptureRequest {
  if (sourceScene == null) {
    throw new TypeError('snapshotBackdrop: sourceScene is required');
  }
  if (!sourceScene.children || !Array.isArray(sourceScene.children.list)) {
    throw new TypeError('snapshotBackdrop: sourceScene.children.list missing');
  }
  if (hasFlashOverlay(sourceScene.children.list)) {
    throw new Error(
      'snapshotBackdrop: encounter flashOverlay still in display list; PR 2 contract is destroy() before captureFrom()',
    );
  }
  if (!(opts.width > 0) || !(opts.height > 0)) {
    throw new RangeError(
      `snapshotBackdrop: viewport ${opts.width}x${opts.height} is degenerate`,
    );
  }
  if (typeof opts.textureKey !== 'string' || opts.textureKey.length === 0) {
    throw new TypeError('snapshotBackdrop: textureKey must be a non-empty string');
  }
  return {
    width: Math.floor(opts.width),
    height: Math.floor(opts.height),
    textureKey: opts.textureKey,
    timeoutMs: opts.timeoutMs ?? defaultTimeoutForArea(opts.width, opts.height),
    depth: opts.depth ?? DEFAULT_DEPTH,
  };
}

/**
 * Capture state machine. Drives the Phaser shell's Promise lifecycle:
 *
 *   idle      -- START    --> awaiting
 *   awaiting  -- COMPLETE --> ready    (terminal)
 *   awaiting  -- TIMEOUT  --> timeout  (terminal)
 *   awaiting  -- ERROR    --> error    (terminal)
 *
 * Terminal states are sticky so a late 'render' event after timeout
 * cannot mis-resolve the Promise, and the timeout firing twice (e.g.
 * a resize storm) cannot unwind ready state.
 */
export function resolveCaptureState(
  state: CaptureState,
  event: CaptureEvent,
): CaptureState {
  if (state === 'ready' || state === 'timeout' || state === 'error') {
    return state;
  }
  if (state === 'idle') {
    return event === 'START' ? 'awaiting' : 'idle';
  }
  // state === 'awaiting'
  if (event === 'COMPLETE') return 'ready';
  if (event === 'TIMEOUT') return 'timeout';
  if (event === 'ERROR') return 'error';
  return 'awaiting';
}

/**
 * Monotonic key generator scoped to the module. Each captured
 * DynamicTexture gets its own key so transient overlap during the
 * scene-stop → scene-launch chain (eng-locked #9: accept 2x peak GPU
 * memory) cannot collide in the global TextureManager.
 *
 * `__resetSnapshotKeySeq` is test-only.
 */
let _seq = 0;
const KEY_PREFIX = '__snapshotBackdrop_';
export function generateSnapshotKey(): string {
  _seq += 1;
  return `${KEY_PREFIX}${_seq}`;
}
export function __resetSnapshotKeySeq(): void {
  _seq = 0;
}

/** Opaque identity token for a Phaser.Scene. Tests pass POJOs. */
export type SceneRef = object;

export interface OwnershipTransferRequest {
  readonly textureKey: string;
  readonly currentScene: SceneRef;
  readonly targetScene: SceneRef;
}

export interface OwnershipTransferPlan {
  readonly noop: boolean;
  readonly destroyOldImage: boolean;
  readonly createOnTarget: boolean;
  readonly textureKey: string;
}

/**
 * Compute the steps required to transfer a snapshot Image from one
 * scene to another while keeping the underlying DynamicTexture alive
 * in the global TextureManager (eng-locked #10).
 *
 * Idempotent: if currentScene === targetScene the plan is a no-op,
 * which lets a defensive double-transfer inside modal create() be
 * harmless.
 */
export function computeOwnershipTransfer(
  req: OwnershipTransferRequest,
): OwnershipTransferPlan {
  if (typeof req.textureKey !== 'string' || req.textureKey.length === 0) {
    throw new TypeError('computeOwnershipTransfer: textureKey is required');
  }
  if (req.currentScene === req.targetScene) {
    return {
      noop: true,
      destroyOldImage: false,
      createOnTarget: false,
      textureKey: req.textureKey,
    };
  }
  return {
    noop: false,
    destroyOldImage: true,
    createOnTarget: true,
    textureKey: req.textureKey,
  };
}
